/**
 * The attribute-operator vocabulary. One declaration is the source of truth
 * for every derived read: the dropdown option groups in attribute-filter.ts
 * and the per-surface allow-lists the condition rows build from `family`.
 *
 * Values match the backend's canonical spelling (services core/operators.go
 * `Opstring()`). `arity` is how many values the operator takes on its
 * right-hand side and agrees with the backend's `IsZeroValue`/`IsTwoValue`;
 * negations inherit it by construction.
 *
 * Negation is a pairing, not wire syntax: the `!` prefix is this UI's
 * spelling, while the wire carries a base operator wrapped in `{ not: ... }`.
 * Each format plugin owns that encoding.
 */

export type OperatorArity = 0 | 1 | 2;

// The semantic grouping the surfaces compose allow-lists from. Distinct from a
// select-group heading, which is per-surface (see attribute-filter.ts).
export type OperatorFamily =
  | 'equality'
  | 'inequality'
  | 'existence'
  | 'string'
  | 'dateExistence'
  | 'dateInequality'
  | 'extraDateInequality'
  | 'email'
  | 'array'
  | 'oneOf';

/**
 * A bare operator entry: the vocabulary's own value/label pair, with no
 * dropdown state. Distinct from the dropdown's `OperatorOption` in
 * operator-groups.ts, which adds per-field `isDisabled`.
 */
export type OperatorLabel = { value: string; label: string };

/**
 * An operator worded as a change, for the sentence that describes an
 * attribute-changed trigger: `changes to be equal to`, not `is equal to`.
 */
type WordedOperator = OperatorLabel & { updateLabel: string };

type OperatorDefinition = WordedOperator & {
  arity: OperatorArity;
  family: OperatorFamily;
  negation?: WordedOperator;
};

export const OPERATOR_DEFINITIONS = [
  {
    value: 'eq',
    label: 'is equal to',
    updateLabel: 'changes to be equal to',
    arity: 1,
    family: 'equality',
    negation: { value: '!eq', label: 'is not equal to', updateLabel: 'changes to not equal to' },
  },
  { value: 'gt', label: 'is greater than', updateLabel: 'changes to be greater than', arity: 1, family: 'inequality' },
  { value: 'lt', label: 'is less than', updateLabel: 'changes to be less than', arity: 1, family: 'inequality' },
  { value: 'bw', label: 'is between', updateLabel: 'changes to be between', arity: 2, family: 'inequality' },
  {
    value: 'exists',
    label: 'exists',
    updateLabel: 'changes and any value exists',
    arity: 0,
    family: 'existence',
    negation: { value: '!exists', label: 'does not exist', updateLabel: 'changes and no value exists' },
  },
  {
    value: 'contains',
    label: 'contains',
    updateLabel: 'changes to contain',
    arity: 1,
    family: 'string',
    negation: { value: '!contains', label: 'does not contain', updateLabel: 'changes to no longer contain' },
  },
  {
    value: 'timestamp',
    label: 'is a timestamp',
    updateLabel: 'changes to be a timestamp',
    arity: 0,
    family: 'dateExistence',
    negation: { value: '!timestamp', label: 'is not a timestamp', updateLabel: 'changes to be not a timestamp' },
  },
  {
    value: 'timestamp_gt',
    label: 'is a timestamp after',
    updateLabel: 'changes to be a timestamp after',
    arity: 1,
    family: 'dateInequality',
  },
  {
    value: 'timestamp_lt',
    label: 'is a timestamp before',
    updateLabel: 'changes to be a timestamp before',
    arity: 1,
    family: 'dateInequality',
  },
  // Absolute timestamp operators: same labels as the relative pair, reached
  // through a variant select rather than the operator dropdown.
  {
    value: 'timestamp_gta',
    label: 'is a timestamp after',
    updateLabel: 'changes to be a timestamp after',
    arity: 1,
    family: 'dateInequality',
  },
  {
    value: 'timestamp_lta',
    label: 'is a timestamp before',
    updateLabel: 'changes to be a timestamp before',
    arity: 1,
    family: 'dateInequality',
  },
  {
    value: 'timestamp_bw',
    label: 'is a timestamp between',
    updateLabel: 'changes to be a timestamp between',
    arity: 2,
    family: 'extraDateInequality',
  },
  {
    value: 'email_valid',
    label: 'is valid and exists',
    updateLabel: 'changes to be valid',
    arity: 0,
    family: 'email',
    negation: { value: '!email_valid', label: "is not valid or doesn't exist", updateLabel: 'changes to be not valid' },
  },
  {
    value: 'contained_in_array',
    label: 'is contained in',
    updateLabel: 'changes and is contained in',
    arity: 1,
    family: 'array',
    negation: {
      value: '!contained_in_array',
      label: 'is not contained in',
      updateLabel: 'changes and no longer is contained in',
    },
  },
  {
    value: 'one_of',
    label: 'is one of',
    updateLabel: 'changes to one of',
    arity: 1,
    family: 'oneOf',
    negation: { value: '!one_of', label: 'is not one of', updateLabel: 'changes to none of' },
  },
] as const satisfies readonly OperatorDefinition[];

const ARITY_BY_OPERATOR: Map<string, OperatorArity> = (() => {
  const arities = new Map<string, OperatorArity>();
  for (const definition of OPERATOR_DEFINITIONS) {
    arities.set(definition.value, definition.arity);
    if ('negation' in definition) {
      // A negation takes the same values as the operator it negates.
      arities.set(definition.negation.value, definition.arity);
    }
  }
  return arities;
})();

// A '!' prefix on a UI operator does not always mean a declared negation: the
// wire negates by wrapping a filter in `{ not: ... }`, and unwrapping that
// prefixes whatever operator it held — including ones with no negated form of
// their own, like `bw`. Negating never changes arity or family, so both reads
// fall back to the base operator.
function lookUp<TValue>(table: Map<string, TValue>, operator: string): TValue | undefined {
  const exact = table.get(operator);
  if (exact !== undefined) {
    return exact;
  }
  return operator.startsWith('!') ? table.get(operator.slice(1)) : undefined;
}

// How many values the operator takes, or undefined for anything outside the
// vocabulary. Tolerant on purpose: the callers are checking wire payloads and
// user input, where an unrecognized operator is a case to handle rather than a
// programming error.
export function operatorArity(operator: string | null | undefined): OperatorArity | undefined {
  if (operator == null) {
    return undefined;
  }
  return lookUp(ARITY_BY_OPERATOR, operator);
}

const FAMILY_BY_OPERATOR: Map<string, OperatorFamily> = (() => {
  const families = new Map<string, OperatorFamily>();
  for (const definition of OPERATOR_DEFINITIONS) {
    families.set(definition.value, definition.family);
    if ('negation' in definition) {
      families.set(definition.negation.value, definition.family);
    }
  }
  return families;
})();

// The family a declared operator belongs to, or undefined for anything outside
// the vocabulary. A declared negation shares its base's family.
//
// Strict where `operatorArity` is tolerant, and the asymmetry is deliberate:
// callers use this to decide whether a surface can *offer* an operator, so a
// `not`-wrapped `bw` arriving as `!bw` must read as unknown — there is no `!bw`
// to put in a dropdown. Arity, by contrast, is still knowable for it.
export function familyOfOperator(operator: string | null | undefined): OperatorFamily | undefined {
  if (operator == null) {
    return undefined;
  }
  return FAMILY_BY_OPERATOR.get(operator);
}

// Whether a surface that edits these families can edit this operator. Listing
// families positively is deliberate: a family added to the vocabulary later is
// unsupported until someone says otherwise, because a row must never offer an
// operator its dropdown can't render.
export function isOperatorInFamilies(operator: string, families: ReadonlySet<OperatorFamily>): boolean {
  const family = familyOfOperator(operator);
  return family != null && families.has(family);
}

export function isOneOfOperator(operator: string | null | undefined): boolean {
  return operator === 'one_of' || operator === '!one_of';
}

// The dateInequality family pairs each relative operator (value = signed
// seconds from now, negative = ago) with an absolute variant (value = a unix
// timestamp). Only the relative pair appears in the operator dropdown, so the
// pairing is vocabulary data, not view state.
export const TIMESTAMP_VARIANTS = [
  { relative: 'timestamp_gt', absolute: 'timestamp_gta' },
  { relative: 'timestamp_lt', absolute: 'timestamp_lta' },
] as const;

export type TimestampVariant = 'relative' | 'absolute';

export function timestampVariantOf(operator: string): TimestampVariant | undefined {
  for (const pair of TIMESTAMP_VARIANTS) {
    if (pair.relative === operator) {
      return 'relative';
    }
    if (pair.absolute === operator) {
      return 'absolute';
    }
  }
  return undefined;
}

/**
 * The paired operator for the requested variant, or the operator unchanged when
 * it is not one of the paired timestamp inequalities. Derived from the same
 * `TIMESTAMP_VARIANTS` table the variant read uses, so a new pair added there
 * rewrites in both directions without a second edit.
 */
export function timestampOperatorForVariant(operator: string, variant: TimestampVariant): string {
  for (const pair of TIMESTAMP_VARIANTS) {
    if (pair.relative === operator || pair.absolute === operator) {
      return variant === 'absolute' ? pair.absolute : pair.relative;
    }
  }
  return operator;
}

/**
 * The relative form of a paired timestamp operator. The operator dropdown only
 * ever offers the relative pair, so an absolute variant off the wire has to
 * normalize to its relative twin to appear selected.
 */
export function relativeTimestampOperatorOf(operator: string): string {
  return timestampOperatorForVariant(operator, 'relative');
}

const UPDATE_LABEL_BY_OPERATOR: Map<string, string> = (() => {
  const labels = new Map<string, string>();
  for (const definition of OPERATOR_DEFINITIONS) {
    labels.set(definition.value, definition.updateLabel);
    if ('negation' in definition) {
      labels.set(definition.negation.value, definition.negation.updateLabel);
    }
  }
  return labels;
})();

const STATE_LABEL_BY_OPERATOR: ReadonlyMap<string, string> = (() => {
  const labels = new Map<string, string>();
  for (const definition of OPERATOR_DEFINITIONS) {
    labels.set(definition.value, definition.label);
    if ('negation' in definition) {
      labels.set(definition.negation.value, definition.negation.label);
    }
  }
  return labels;
})();

/** The operator worded as a state, `is equal to`, or undefined outside the vocabulary. */
export function operatorStateLabel(operator: string | null | undefined): string | undefined {
  return operator == null ? undefined : STATE_LABEL_BY_OPERATOR.get(operator);
}

/**
 * The operator worded as a change, or undefined outside the vocabulary.
 *
 * A negation the vocabulary does not declare (`!gt`, from a `not`-wrapped
 * filter) negates its base's wording: `changes to not be greater than`.
 */
export function operatorUpdateLabel(operator: string | null | undefined): string | undefined {
  if (operator == null) {
    return undefined;
  }
  const declared = UPDATE_LABEL_BY_OPERATOR.get(operator);
  if (declared !== undefined || !operator.startsWith('!')) {
    return declared;
  }
  const base = UPDATE_LABEL_BY_OPERATOR.get(operator.slice(1));
  if (base === undefined) {
    return undefined;
  }
  const changesTo = 'changes to ';
  return base.startsWith(changesTo) ? `${changesTo}not ${base.slice(changesTo.length)}` : `not ${base}`;
}

// Every operator in a family, each negation directly after its base — the
// order the dropdown option arrays have always been in.
export function operatorsInFamily(family: OperatorFamily): OperatorLabel[] {
  const options: OperatorLabel[] = [];
  for (const definition of OPERATOR_DEFINITIONS) {
    if (definition.family !== family) {
      continue;
    }
    options.push({ value: definition.value, label: definition.label });
    if ('negation' in definition) {
      options.push({ value: definition.negation.value, label: definition.negation.label });
    }
  }
  return options;
}
